import { useCallback, useEffect, useState } from 'react';

import { googleAnalyticsConfig } from '../../../config/googleAnalyticsConfig';
import { useLocalisation } from '../../../services/localisationContext';
import { getWindow } from '../../../services/windowService';
import { useApp } from '../../useApp';
import { useTagCommander } from '../useTagCommander/useTagCommander';
import { gtagQueue } from './gtagQueue';
import { isGaScriptLoaded } from './isGaScriptLoaded';

const wnd = getWindow();

export const useGoogleAnalytics = (activated: boolean) => {
  /* app hooks */
  const { selectedPrivacyCategories } = useTagCommander();
  const { currentLocale } = useLocalisation();
  const { loggedIn } = useApp();

  /* local states */
  const [enabled, setEnabled] = useState<boolean>(isGaScriptLoaded.get());
  const [loaded, setLoaded] = useState<boolean>(isGaScriptLoaded.get());

  /* dispose */
  useEffect(() => {
    return isGaScriptLoaded.subscribe(setLoaded);
  }, []);

  /* enable/disable google analytics */
  useEffect(() => {
    if (activated) {
      if (!selectedPrivacyCategories?.length) {
        return;
      }
      setEnabled(
        selectedPrivacyCategories.some(
          (c) => c === googleAnalyticsConfig.tagCommanderCategoryForGa,
        ),
      );
    }
  }, [selectedPrivacyCategories, activated]);

  /* set user properties */
  useEffect(() => {
    if (!enabled || !loaded || currentLocale?.length === 2) {
      return;
    }
    const { gtag } = wnd;
    gtag('set', 'user_properties', {
      ui_locale: currentLocale,
      ui_logged_in: loggedIn,
    });
  }, [currentLocale, loggedIn, loaded, enabled]);

  /* exported function to send pageView */
  const gaPageView = useCallback(
    (title: string, userProperties?: Record<string, string>) => {
      if (!enabled || !loaded) {
        return;
      }
      const payload = {
        page_title: title,
        page_location: wnd.location.href,
        page_path: wnd.location.pathname,
        send_to: googleAnalyticsConfig.gaMeasurementId,
        user_properties: userProperties,
      };
      gtagQueue.set([['event', 'page_view', payload]]);
    },
    [enabled, loaded],
  );

  const gaRequestConfiguration = useCallback(
    (payload: RequestFormPayload) => {
      if (!enabled || !loaded) {
        return;
      }
      gtagQueue.set([['event', 'request_form', payload]]);
    },
    [enabled, loaded],
  );

  const gaLoginEvent = useCallback(() => {
    gtagQueue.set([['event', 'login']]);
  }, []);

  const gaLogoutEvent = useCallback(() => {
    gtagQueue.set([['event', 'logout']]);
  }, []);

  const gaCreateConfigurationEvent = useCallback((options: CreateConfigurationPayload) => {
    const { articleNumber, templateName } = options;
    if (articleNumber) {
      gtagQueue.set([['event', 'create_configuration', { article_number: articleNumber }]]);
    }
    if (templateName) {
      gtagQueue.set([['event', 'create_configuration', { template_name: templateName }]]);
    }
  }, []);

  return {
    gaPageView,
    gaRequestConfiguration,
    gaLoginEvent,
    gaLogoutEvent,
    gaCreateConfigurationEvent,
  };
};

export type RequestFormPayload = {
  event_category: 'Submit' | 'Open';
  event_label: 'anonymous_user' | 'signed_in_user';
} & Record<string, any>;

type CreateConfigurationPayload = { articleNumber?: string; templateName?: string };

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
// gtag('event', <action>, {
//   'event_category': <category>,
//   'event_label': <label>,
//   'value': <value>
// });

// const gaClick = useCallback(
//   (title: string, path?: string) => {
//     if (!enabled) {
//       return;
//     }
//     console.debug('GA msg: ', {
//       page_title: title,
//       page_location: wnd.location.href,
//       page_path: path || wnd.location.pathname,
//       send_to: googleAnalyticsConfig.gaMeasurementId,
//     });
//     setQueue((val) => {
//       return [
//         ...val,
//         [
//           'event',
//           'click',
//           {
//             // page_title: title,
//             // page_location: wnd.location.href,
//             // page_path: path || wnd.location.pathname,
//             // send_to: googleAnalyticsConfig.gaMeasurementId,
//             // user_properties: {},
//           },
//         ],
//       ];
//     });
//   },
//   [enabled],
// );
